import React, { useState } from 'react';

import { FullPage } from '../components/FullPage';
import { Label } from '../components/Label';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {useMutation, useQuery} from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  ADMIN_CREATE_UNIT_MUTATION,
  ADMIN_GET_UNITS_QUERY,
  AdminUnitsData,
  CreateUnit,
  createUpdateCache, Unit, unitsToTree
} from '../graphql/units';

import {functionIdOptions, setSidcFunctionId, setSidcUnitSize, sidcToSymbol, unitSizeOptions} from '../utils/symbols';
import styled from 'styled-components';

type Inputs = {
  parentId: number,
  name: string,
  sidc: string,
  additionalInfo: string,
};

interface ParentOption {
  id: number
  name: string
}

const UnitSymbolContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing.margin}px;
`;

const UnitSymbol = styled.img`
  height: 5em;
`;

const unitToOptions = (prefix: string, unit: Unit) : ParentOption[] => {
  const name = `${prefix}${unit.name}`
  const option: ParentOption = {
    id: unit.id,
    name
  }
  const childOptions = unit.children?.map(child => unitToOptions(`${name} > `, child))
    .reduce((prev, cur) => {
      return prev.concat(cur)
    }, []) || [];
  return [option, ...childOptions];
}

const formDataToCreateUnitRequest = (eventId: number, factionId: number, formData: Inputs, copyNumber: number): CreateUnit => {
  return {
    eventId,
    factionId,
    parentId: formData.parentId,
    name: `${formData.name}${copyNumber > 1 ? ` ${copyNumber}` : ''}`,
    sidc: formData.sidc,
    additionalInfo: formData.additionalInfo
  }
}

export const NewUnitPage = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [numberOfUnits, setNumberOfUnits] = useState<number>(1);
  const [parentOptions, setParentOptions] = useState<ParentOption[]>([]);
  const [unitSize, setUnitSize] = useState<string>('-');
  const [unitFunction, setUnitFunction] = useState<string>('U');
  const { eventId, factionId } = useParams();
  const search =  new URLSearchParams(useLocation().search);
  const queryParentId = parseInt(search.get('parentId') || '0');

  const { register, handleSubmit, errors, formState, setValue, watch, reset } = useForm<Inputs>({mode: 'onChange'});

  const unitsOnCompleted = (data: AdminUnitsData) => {
    const unitTree = unitsToTree(data.adminGetUnits)
    const parentOptions = unitTree
      .map(unit => unitToOptions('', unit))
      .reduce((prev, cur) => {
        return prev.concat(cur);
      },[]);
    const rootOption: ParentOption = {
      id: 0,
      name: t('units.root')
    }
    setParentOptions([rootOption, ...parentOptions]);

    reset({
      parentId: queryParentId,
      name: '',
      sidc: 'SFGPU-------'
    });
  }

  const sidc = watch('sidc');
  const unitName = watch('name');
  const additionalInfo = watch('additionalInfo');
  const parentId = watch('parentId');

  const unitSizeChanged = ({ target }) => {
    setUnitSize(target.value);
    const newSidc = setSidcUnitSize(sidc, target.value);
    setValue('sidc', newSidc);
  }

  const unitFunctionChanged = ({ target }) => {
    setUnitFunction(target.value);
    const newSidc = setSidcFunctionId(sidc, target.value);
    setValue('sidc', newSidc);
  }

  const numberOfUnitsChanged = ({ target }) => {
    setNumberOfUnits(target.value)
  }

  const sizeOptions = unitSizeOptions(t);
  const functionOptions = functionIdOptions(t);

  useQuery<AdminUnitsData>(ADMIN_GET_UNITS_QUERY, {
    variables: { eventId, factionId },
    onCompleted: unitsOnCompleted
  });
  const [adminCreateUnit, { loading }] = useMutation(ADMIN_CREATE_UNIT_MUTATION);

  const onSubmit = (formData) => {
    Promise.all(
      Array(Number.parseInt(`${numberOfUnits}`)).fill(0).map((e, i) => {
        return formDataToCreateUnitRequest(eventId, factionId, formData, i+1)
      }).map(createUnit => {
        return adminCreateUnit({
          variables: createUnit,
          update: (client, result) => createUpdateCache(eventId, factionId, client, result)
        })
      })
    )
    .then(() => {
      history.push(`/events/${eventId}/factions/${factionId}/units`)
    })
  };

  return (
    <FullPage
      heading={t('units.newUnit')}
      loading={loading}
      closeHandler={() => history.push(`/events/${eventId}/factions/${factionId}/units`)}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <UnitSymbolContainer>
          <UnitSymbol src={`data:image/svg+xml;utf8,${sidcToSymbol(sidc, unitName, additionalInfo)}`} />
        </UnitSymbolContainer>

        <Label htmlFor={'sidc'} hasError={errors.sidc ? 'true' : 'false'}>
          {t('sidc')}{errors.sidc && ' - ' + t('errors.invalid')}
        </Label>
        <input ref={register({ required: true, maxLength: 12, minLength: 12 })} type="text" id="sidc" name="sidc" placeholder={t('sidc')} />

        <Label htmlFor={'unitSize'} hasError={'false'}>
          {t('units.unitSize')}
        </Label>
        <select value={unitSize} onChange={unitSizeChanged} name="unitSize">
          {
            sizeOptions.map(option => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))
          }
        </select>

        <Label htmlFor={'unitFunction'} hasError={'false'}>
          {t('units.unitFunction')}
        </Label>
        <select id="unitFunction" value={unitFunction} onChange={unitFunctionChanged} name="unitFunction">
          {
            functionOptions.map(option => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))
          }
        </select>

        <Label htmlFor={'parentId'} hasError={'false'}>
          {t('units.parent')}
        </Label>
        <select ref={register} defaultValue={parentId} name="parentId">
          {
            parentOptions.map(option => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))
          }
        </select>
        <Label htmlFor={'name'} hasError={errors.name ? 'true' : 'false'}>
          {t('units.unitName')}{errors.name && ' - ' + t('errors.requiredField')}
        </Label>
        <input ref={register({ required: true })} type="text" id="name" name="name" placeholder={t('units.unitName')} />

        <Label htmlFor={'additionalInfo'} hasError={'false'}>
          {t('units.additionalInfo')}
        </Label>
        <input ref={register()} type="text" id="additionalInfo" name="additionalInfo" placeholder={t('units.additionalInfo')} />

        <Label htmlFor={'numberOfUnits'} hasError={'false'}>
          {t('units.numberOfUnits')}
        </Label>
        <input type="number" min={1} max={20} id="numberOfUnits" name="numberOfUnits" value={numberOfUnits} onChange={numberOfUnitsChanged} />

        <input disabled={!formState.isValid} type="submit" value={t('units.createUnit') as string}/>
      </form>

    </FullPage>
  )
}
